import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ConfirmDialog } from '@saas-kit/ui';
import { DataTable, type DataTableColumn } from '@saas-kit/ui/DataTable';

import {
  createCustomerType,
  deleteCustomerType,
  listCustomerTypes,
  updateCustomerType,
  type QmsCustomerType,
} from './api';
import {
  CustomerTypeCreateForm,
  type CustomerTypeCreateInput,
} from './CustomerTypeCreateForm';
import { CustomerTypeRankCell } from './CustomerTypeRankCell';
import { useQmsCrud } from './useQmsCrud';

export function CustomerTypesTab({
  canManage,
  enabled,
}: {
  canManage: boolean;
  enabled: boolean;
}): JSX.Element {
  const { t } = useTranslation('qms');
  const crud = useQmsCrud<QmsCustomerType, CustomerTypeCreateInput>({
    key: 'qms-customer-types',
    list: listCustomerTypes,
    create: createCustomerType,
    update: updateCustomerType,
    remove: deleteCustomerType,
    enabled,
  });

  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const columns: DataTableColumn<QmsCustomerType>[] = [
    { id: 'code', header: t('fields.code'), cell: (r) => r.code },
    { id: 'name', header: t('fields.name'), cell: (r) => r.name },
    {
      id: 'rank',
      header: t('fields.rank'),
      align: 'end',
      exportValue: (r) => r.rank,
      cell: (r) => (
        <CustomerTypeRankCell
          rank={r.rank}
          canManage={canManage}
          onCommit={(rank) => crud.update(r.id, { rank })}
        />
      ),
    },
    {
      id: 'default',
      header: t('fields.default'),
      cell: (r) => (r.is_default ? '★' : ''),
    },
    {
      id: 'status',
      header: t('fields.status'),
      cell: (r) => (r.is_active ? t('status.active') : t('status.inactive')),
    },
    {
      id: 'actions',
      header: '',
      align: 'end',
      exportSkip: true,
      cell: (r) =>
        canManage ? (
          <div className="flex justify-end gap-2">
            {!r.is_default ? (
              <Button
                variant="ghost"
                onClick={() => crud.update(r.id, { is_default: true })}
              >
                {t('fields.default')}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              onClick={() => crud.update(r.id, { is_active: !r.is_active })}
            >
              {r.is_active ? t('actions.deactivate') : t('actions.activate')}
            </Button>
            <Button variant="ghost" onClick={() => setPendingDelete(r.id)}>
              {t('actions.delete')}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      {canManage ? (
        <CustomerTypeCreateForm
          creating={crud.creating}
          onCreate={crud.create}
        />
      ) : null}

      <DataTable
        data={crud.items}
        columns={columns}
        rowKey={(r) => r.id}
        selectable={canManage}
        isLoading={crud.isLoading}
        error={crud.isError ? t('errors.view_denied') : null}
        emptyText={t('empty')}
        bulkActions={
          canManage
            ? [
                {
                  id: 'delete',
                  label: t('actions.delete'),
                  variant: 'destructive',
                  onRun: (rows) => crud.removeMany(rows.map((r) => r.id)),
                },
              ]
            : []
        }
      />

      {pendingDelete ? (
        <ConfirmDialog
          title={t('confirm.delete_title')}
          body={t('confirm.delete_body')}
          confirmLabel={t('confirm.confirm')}
          cancelLabel={t('confirm.cancel')}
          variant="destructive"
          isBusy={crud.removing}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            crud.remove(pendingDelete);
            setPendingDelete(null);
          }}
        />
      ) : null}
    </div>
  );
}
